import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { FiX, FiSearch, FiArrowUp } from 'react-icons/fi';

// Styled Components
const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  
  @media (min-width: 769px) {
    background-color: rgba(0, 0, 0, 0.6);
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SheetContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1001;
  background-color: var(--module-surface);
  border: 1px solid var(--module-border);
  border-radius: var(--card-radius, 24px);
  box-shadow: var(--shadow-md);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateY(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
  padding-bottom: env(safe-area-inset-bottom);
  
  @media (min-width: 769px) {
    /* Desktop: center as modal */
    bottom: auto;
    top: 50%;
    left: 50%;
    transform: translate(-50%, ${props => props.$isOpen ? '-50%' : '-40%'});
    max-width: 500px;
    width: 90%;
    max-height: 70vh;
    border-radius: var(--card-radius, 24px);
    box-shadow: var(--shadow-md);
    opacity: ${props => props.$isOpen ? 1 : 0};
    pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  padding-top: 1.75rem;
  border-bottom: 1px solid var(--border-color);
  min-height: 60px;
  touch-action: pan-y;
  cursor: ${props => props.$dragging ? 'grabbing' : 'grab'};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0.75rem;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 4px;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
`;

const SheetTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid var(--module-border);
  background-color: var(--module-surface);
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--control-radius, 12px);
  transition: color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background-color: var(--module-bg-warm);
    transform: translateY(-1px);
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 3px;
  }
`;

const SearchContainer = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border: 1px solid var(--module-border);
  border-radius: var(--control-radius, 14px);
  background-color: var(--module-surface);
  color: var(--text-primary);
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus-visible {
    outline: none;
    border-color: var(--module-accent-gold);
    box-shadow: 0 0 0 3px rgba(176, 141, 87, 0.2);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const SheetContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--background-secondary);
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: var(--text-muted);
  }
`;

const SectionItem = styled.button`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 1rem 1.5rem;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  min-height: 44px;
  font-family: 'Inter', sans-serif;
  border: 1px solid transparent;
  border-bottom: 1px solid var(--module-border);
  position: relative;
  border-radius: var(--control-radius, 14px);
  
  &:hover {
    background-color: var(--module-bg-warm);
    transform: translateY(-1px);
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
  
  ${props => props.$isActive && css`
    background-color: rgba(176, 141, 87, 0.08);
    color: var(--module-accent-gold);
    font-weight: 600;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background-color: var(--module-accent-gold);
    }
    
    .section-icon {
      color: var(--module-accent-gold);
    }
  `}
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--text-secondary);
  flex-shrink: 0;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const SectionLabel = styled.span`
  flex: 1;
  font-size: 1rem;
  color: var(--text-primary);
  font-weight: ${props => props.$isActive ? 600 : 400};
`;

const EmptyState = styled.div`
  padding: 3rem 1.5rem;
  text-align: center;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
`;

// Focus trap hook
const useFocusTrap = (isOpen, containerRef) => {
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }, [isOpen, containerRef]);
};

// Main Component
const JumpToSheet = ({
  isOpen,
  title = 'Jump to',
  sections = [],
  activeId = null,
  onClose,
  onSelect
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragCurrentY, setDragCurrentY] = useState(0);
  
  const sheetRef = useRef(null);
  const headerRef = useRef(null);
  const searchInputRef = useRef(null);

  useFocusTrap(isOpen, sheetRef);

  // Filter sections based on search query
  const filteredSections = sections.filter(section =>
    section.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Reset search when sheet closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Focus search input when sheet opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Drag to close handlers
  const handleTouchStart = useCallback((e) => {
    setIsDragging(true);
    setDragStartY(e.touches[0].clientY);
    setDragCurrentY(e.touches[0].clientY);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - dragStartY;
    
    if (deltaY > 0) {
      setDragCurrentY(currentY);
      if (sheetRef.current) {
        sheetRef.current.style.transform = `translateY(${deltaY}px)`;
      }
    }
  }, [isDragging, dragStartY]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    const deltaY = dragCurrentY - dragStartY;
    const timeDelta = Date.now() - (window.dragStartTime || Date.now());
    const velocity = Math.abs(deltaY) / Math.max(timeDelta, 1);
    
    if (deltaY > 80 || velocity > 0.5) {
      onClose();
    } else {
      // Snap back
      if (sheetRef.current) {
        sheetRef.current.style.transform = 'translateY(0)';
      }
    }
    
    setIsDragging(false);
    setDragStartY(0);
    setDragCurrentY(0);
    window.dragStartTime = null;
  }, [isDragging, dragCurrentY, dragStartY, onClose]);

  // Track drag start time
  useEffect(() => {
    if (isDragging && !window.dragStartTime) {
      window.dragStartTime = Date.now();
    }
  }, [isDragging]);

  // Reset transform when opening/closing
  useEffect(() => {
    if (!isDragging && sheetRef.current) {
      sheetRef.current.style.transform = '';
    }
  }, [isOpen, isDragging]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleSectionClick = useCallback((sectionId) => {
    if (sectionId === 'back-to-top') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      onClose();
    } else {
      // Call onSelect first, then close
      onSelect(sectionId);
      onClose();
    }
  }, [onSelect, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <Backdrop
        $isOpen={isOpen}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <SheetContainer
        ref={sheetRef}
        $isOpen={isOpen}
        role="dialog"
        aria-modal="true"
        aria-labelledby="jump-to-sheet-title"
      >
        <SheetHeader
          ref={headerRef}
          $dragging={isDragging}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <SheetTitle id="jump-to-sheet-title">{title}</SheetTitle>
          <CloseButton
            onClick={onClose}
            aria-label="Close"
          >
            <FiX size={24} />
          </CloseButton>
        </SheetHeader>
        
        <SearchContainer>
          <div style={{ position: 'relative' }}>
            <SearchIcon>
              <FiSearch size={20} />
            </SearchIcon>
            <SearchInput
              ref={searchInputRef}
              type="text"
              placeholder="Search sections"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search sections"
            />
          </div>
        </SearchContainer>
        
        <SheetContent>
          {/* Back to top - always shown first */}
          <SectionItem
            onClick={() => handleSectionClick('back-to-top')}
            $isActive={false}
          >
            <SectionIcon className="section-icon">
              <FiArrowUp size={20} />
            </SectionIcon>
            <SectionLabel $isActive={false}>
              Back to top
            </SectionLabel>
          </SectionItem>
          
          {/* Sections list */}
          {filteredSections.length > 0 ? (
            filteredSections.map((section) => (
              <SectionItem
                key={section.id}
                onClick={() => handleSectionClick(section.id)}
                $isActive={activeId === section.id}
                aria-current={activeId === section.id ? 'true' : undefined}
              >
                {section.icon && (
                  <SectionIcon className="section-icon">
                    {section.icon}
                  </SectionIcon>
                )}
                <SectionLabel $isActive={activeId === section.id}>
                  {section.label}
                </SectionLabel>
              </SectionItem>
            ))
          ) : searchQuery ? (
            <EmptyState>
              No results found
            </EmptyState>
          ) : null}
        </SheetContent>
      </SheetContainer>
    </>,
    document.body
  );
};

export default JumpToSheet;
